import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';
import { useSessionStore } from '@/stores/session.store';

import { profileRepository } from '../api/profile.repository';
import type { Profile, ProfileWriteInput } from '../model/profile';

export const profileKeys = {
  all: ['profile'] as const,
  byId: (id: string) => [...profileKeys.all, 'byId', id] as const,
  providers: () => [...profileKeys.all, 'providers'] as const,
};

/** The signed-in user's profile (null until onboarded). */
export function useMyProfile() {
  const userId = useSessionStore((s) => s.userId);
  return useQuery({
    queryKey: profileKeys.byId(userId ?? 'anon'),
    queryFn: () => unwrap(profileRepository.getById(userId as string)),
    enabled: Boolean(userId),
  });
}

export function useUpsertProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: ProfileWriteInput) => unwrap(profileRepository.upsert(input)),
    onSuccess: (profile: Profile) => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.byId(profile.id) });
    },
  });
}

export function useGiveLocationConsent() {
  const queryClient = useQueryClient();
  const userId = useSessionStore((s) => s.userId);
  return useMutation({
    mutationFn: () => unwrap(profileRepository.giveLocationConsent()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileKeys.byId(userId ?? 'anon') });
    },
  });
}

export function useProviders() {
  return useQuery({
    queryKey: profileKeys.providers(),
    queryFn: () => unwrap(profileRepository.listProviders()),
  });
}

/** Phase 5 (D-013): admin browser lists every profile. */
export function useAllProfiles() {
  return useQuery({
    queryKey: [...profileKeys.all, 'all'] as const,
    queryFn: () => unwrap(profileRepository.listAllProfiles()),
  });
}

export function useExportMyData() {
  return useMutation({ mutationFn: () => unwrap(profileRepository.exportMyData()) });
}

export function useRequestDeletion() {
  return useMutation({ mutationFn: () => unwrap(profileRepository.requestDeletion()) });
}
